import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import {
    CircularProgress,
    Icon,
    Switch,
    Typography,
    withStyles
} from '@material-ui/core';
import ContentHeader from '../components/ContentHeader';
import ButtonLink from '../components/ButtonLink';
import globalStyles from '../styles';

const styles = theme => ({
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px',
        marginBottom: '60px'
    },
    main: {
        padding: '30px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px'
    },
    switch: {
        display: 'flex',
        alignItems: 'center'
    },
    error: {
        color: 'red'
    },
    link: {
        ...globalStyles.link,
        fontWeight: 'bold'
    }
});

const TicketNotifications = ({ match, classes }) => {
    const {
        params: { ticketId }
    } = match;

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [ticket, setTicket] = useState(undefined);
    const [notify, setNotify] = useState(false);
    const [hasNotifyError, setHasNotifyError] = useState(false);
    const [isNotifyLoading, setIsNotifyLoading] = useState(false);

    const loadData = async () => {
        setIsLoading(true);

        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const ticketResponse = await axios.get(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/customer-ticket/user-id/${
                    currentSession.getAccessToken().payload.sub
                }/${ticketId}?include_notifications=true`,
                {
                    headers
                }
            );

            setIsLoading(false);
            setTicket(ticketResponse.data);
            setNotify(ticketResponse.data.notify || false);
            setHasError(false);
        } catch (e) {
            setIsLoading(false);
            setHasError(true);
        }
    };

    const updateNotifications = newNotify => async event => {
        event.preventDefault();
        setIsNotifyLoading(true);

        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const res = await axios.put(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/customer-ticket/user-id/${
                    currentSession.getAccessToken().payload.sub
                }/${ticketId}/boarding-notifications`,
                {
                    notify: newNotify
                },
                {
                    headers
                }
            );

            if (res.status !== 200) {
                throw Error;
            }

            setIsNotifyLoading(false);
            setHasNotifyError(false);
            setNotify(newNotify);
        } catch (e) {
            setIsNotifyLoading(false);
            setHasNotifyError(true);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    if (isLoading) {
        return (
            <CircularProgress
                id="loading-spinner"
                className={classes.spinner}
            />
        );
    }

    const renderInner = () => {
        if (hasError || !ticket) {
            return (
                <Typography className={classes.error}>
                    There was a problem loading your tickets. Please try again
                    later.
                </Typography>
            );
        }

        if (!ticket.has_boarding_notifications) {
            return (
                <>
                    <Typography variant="body2">
                        Your transport provider has disabled boarding
                        notifications for this service.
                    </Typography>
                    <Typography variant="body2">
                        Please contact your transport provider directly for more
                        information.
                    </Typography>
                </>
            );
        }

        return (
            <>
                <div className={classes.switch}>
                    <Typography variant="body2">
                        EMAIL BOARDING NOTIFICATIONS
                    </Typography>
                    <Switch
                        color="primary"
                        checked={notify}
                        onClick={updateNotifications(!notify)}
                        disabled={isNotifyLoading}
                    />
                </div>
                <Typography variant="body2" style={{ height: 38 }}>
                    <a
                        href="https://help.shuttleid.uk/en/articles/8037680-boarding-notifications"
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        How notifications work
                    </a>
                </Typography>
                {hasNotifyError && (
                    <Typography variant="body2" className={classes.error}>
                        There was an issue updating your notification
                        preferences. Please try again later.
                    </Typography>
                )}
            </>
        );
    };

    return (
        <div className={classes.root}>
            <ContentHeader
                text="Ticket notifications"
                id="ticket-details-heading"
            />
            <div className={classes.main}>
                {renderInner()}
                <ButtonLink
                    className={classes.button}
                    variant="outlined"
                    color="primary"
                    to={`/ticket/${ticketId}`}
                >
                    <Icon className={classes.icon}>arrow_left</Icon>
                    Back to ticket details
                </ButtonLink>
            </div>
        </div>
    );
};

export default withStyles(styles)(TicketNotifications);
